import React, { useState } from "react";
import { useParams } from "react-router-dom";
import moment from "moment";
import { useGetEmployeeMonthViewQuery } from "../../../../store/appApi";
import PageTitle from "../../../layouts/PageTitle";
import { formatWorkDuration } from "./attendanceUtils";
import ManualEntryNote, { ManualMark } from "./ManualEntryNote";

const COLOR_MAP = {
  PRESENT: "#2bc155",
  PARTIAL: "#f6a821",
  ON_LEAVE: "#3f9ae0",
  ON_LEAVE_FIRST_HALF: "#0ea5e9",
  ON_LEAVE_SECOND_HALF: "#0284c7",
  HALF_DAY_LEAVE: "#14b8a6",
  HALF_DAY_PRESENT: "#f59e0b",
  PENDING: "#ff9f43",
  PENDING_FIRST_HALF: "#ff9f43",
  PENDING_SECOND_HALF: "#ff9f43",
  ABSENT: "#f32d2d",
  UPCOMING: "#64748b",
  HOLIDAY: "#8b5cf6",
};

const toMonthStr = (d) => {
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${d.getFullYear()}-${m}`;
};

const EmployeeMonthView = () => {
  const { id } = useParams();
  const [monthDate, setMonthDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);

  const month = toMonthStr(monthDate);
  const { data, isLoading } = useGetEmployeeMonthViewQuery({ id, month });

  const days = data?.days || [];
  const summary = data?.summary || {};
  const employee = data?.employee || {};

  const firstDay = new Date(
    monthDate.getFullYear(),
    monthDate.getMonth(),
    1,
  ).getDay();
  const blanks = Array(firstDay).fill(null);

  const sel = selectedDay && days.find((d) => d.date === selectedDay);

  return (
    <>
      <PageTitle activeMenu="Employee Month View" motherMenu="Attendance" />
      <div className="row">
        {/* Summary bar */}
        <div className="col-12 mb-3">
          <div className="card">
            <div className="card-body py-3">
              <h6 className="mb-3">{employee.name || id}</h6>
              <div className="row g-2 text-center">
                {[
                  { label: "Present", key: "present", color: "#2bc155" },
                  { label: "Partial", key: "partial", color: "#f6a821" },
                  {
                    label: "First Half Day",
                    key: "halfDayPresent",
                    color: "#f59e0b",
                  },
                  { label: "Half Day Leave", key: "halfDay", color: "#14b8a6" },
                  { label: "On Leave", key: "onLeave", color: "#3f9ae0" },
                  { label: "Absent", key: "absent", color: "#f32d2d" },
                  { label: "Upcoming", key: "upcoming", color: "#aaa" },
                ].map((s) => (
                  <div className="col" key={s.key}>
                    <h5 className="mb-0" style={{ color: s.color }}>
                      {summary[s.key] ?? 0}
                    </h5>
                    <small className="text-muted">{s.label}</small>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Mini calendar */}
        <div className="col-xl-12">
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between">
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => {
                  const d = new Date(monthDate);
                  d.setMonth(d.getMonth() - 1);
                  setMonthDate(d);
                  setSelectedDay(null);
                }}
              >
                &lsaquo;
              </button>
              <h5 className="mb-0">
                {monthDate.toLocaleString("default", {
                  month: "long",
                  year: "numeric",
                })}
              </h5>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => {
                  const d = new Date(monthDate);
                  d.setMonth(d.getMonth() + 1);
                  setMonthDate(d);
                  setSelectedDay(null);
                }}
              >
                &rsaquo;
              </button>
            </div>
            <div className="card-body p-2">
              {isLoading ? (
                <div className="text-center py-4">Loading…</div>
              ) : (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(7,1fr)",
                    gap: 4,
                  }}
                >
                  {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
                    (d) => (
                      <div
                        key={d}
                        className="text-center fw-bold small text-muted py-1"
                      >
                        {d}
                      </div>
                    ),
                  )}
                  {blanks.map((_, i) => (
                    <div key={`b${i}`} />
                  ))}
                  {days.map((day) => (
                    <div
                      key={day.date}
                      onClick={() =>
                        setSelectedDay(
                          day.date === selectedDay ? null : day.date,
                        )
                      }
                      style={{
                        background: (COLOR_MAP[day.tag] || "#ccc") + "22",
                        borderLeft: `3px solid ${COLOR_MAP[day.tag] || "#ccc"}`,
                        borderRadius: 4,
                        padding: "5px 4px",
                        cursor: "pointer",
                        outline:
                          selectedDay === day.date
                            ? `2px solid ${COLOR_MAP[day.tag]}`
                            : "none",
                        minHeight: 52,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <div className="fw-bold" style={{ fontSize: 13 }}>
                        {parseInt(day.date.split("-")[2])}
                        <ManualMark entry={day.manualEntry} />
                      </div>
                      <div
                        style={{
                          fontSize: 9,
                          fontWeight: 600,
                          color: day.holiday ? "#8b5cf6" : COLOR_MAP[day.tag],
                          textAlign: "center",
                          lineHeight: 1.1,
                          marginTop: 2,
                        }}
                      >
                        {day.holiday || day.tag?.replace(/_/g, " ")}
                      </div>
                      {day.workDurationMinutes != null && (
                        <div
                          style={{
                            fontSize: 8,
                            color: "#6b7280",
                            textAlign: "center",
                          }}
                        >
                          {formatWorkDuration(day.workDurationMinutes)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Detail panel */}
        {sel && (
          <div className="col-xl-4">
            <div className="card">
              <div className="card-header">
                <h6 className="mb-0">{sel.date}</h6>
              </div>
              <div className="card-body">
                <span
                  className="badge mb-2"
                  style={{
                    background: COLOR_MAP[sel.tag],
                    paddingHorizontal: 8,
                    paddingVertical: 4,
                  }}
                >
                  {sel.tag?.replace(/_/g, " ")}
                </span>
                {sel.workDurationMinutes != null && (
                  <span className="text-muted ms-2 small">
                    {formatWorkDuration(sel.workDurationMinutes)}
                  </span>
                )}
                {sel.punchIn && (
                  <p className="mb-1">
                    <strong>Punch In:</strong>{" "}
                    {moment(sel.punchIn).format("DD-MM-YYYY HH:mm:ss")}
                  </p>
                )}
                {sel.punchOut && (
                  <p className="mb-1">
                    <strong>Punch Out:</strong>{" "}
                    {moment(sel.punchOut).format("DD-MM-YYYY HH:mm:ss")}
                  </p>
                )}
                {sel.workDurationMinutes != null && (
                  <p className="mb-1">
                    <strong>Work Hours:</strong>{" "}
                    {formatWorkDuration(sel.workDurationMinutes)}
                  </p>
                )}
                {sel.selfieUrl && (
                  <img
                    src={sel.selfieUrl}
                    alt="selfie"
                    className="img-fluid rounded mt-2"
                  />
                )}
                <ManualEntryNote entry={sel.manualEntry} />
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default EmployeeMonthView;
